import React from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  Indicator,
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from '../ImpactIndicator/utils';

type BeneficiaryTypes = 'human' | 'animal' | 'nonHuman';

export const getIndicatorType = (
  indicator: IndicatorWithUpdate | Indicator,
): BeneficiaryTypes => {
  if (indicator.isHuman) return 'human';
  if (indicator.name.toLowerCase().includes('animal')) return 'animal';
  return 'nonHuman';
};

const exampleText: {
  [id in BeneficiaryTypes]: {
    [id in IndicatorSectionKey]: string[];
  };
} = {
  human: {
    capacityDevelopment: [],
    reach: [
      'Participate in at least 80% of the training sessions.',
      'Receive food assistance at least once a month for three consecutive months.',
      'Attend at least one counseling session.',
    ],
    learn: [
      'Improve their literacy skills by at least one level based on standardized assessments.',
      'Successfully develop a business plan by the end of the training.',
    ],
    act: [
      'Apply to at least three job openings per week.',
      'Create and stick to a monthly budget.',
      'Regularly practice skills learned in our training sessions.',
    ],
    catalyzed: [],
    succeed: [
      'Secure full-time, upwardly mobile employment at a living wage (per the MIT Living Wage Calculator).',
      'Observably improve their behavior, academic performance, and social skills.',
      'Achieving and maintaining a healthy weight, blood pressure, or blood sugar levels.',
    ],
  },
  animal: {
    capacityDevelopment: [],
    reach: [
      'Adopted during this program year',
      'Received free veterinary care',
      'Live in and benefit from the restored habitat',
    ],
    learn: [],
    act: [],
    catalyzed: [],
    succeed: [
      'Placed in a safe and stable home environment',
      'Number of animals rehabilitated',
      'Animals gain an improved quality of life',
    ],
  },
  nonHuman: {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    catalyzed: [],
    succeed: [],
  },
};

export const ghostText = (
  indicator: IndicatorWithUpdate | Indicator,
  programName: string,
  beneficiaryTerm: string | undefined,
  intermediaries: string | undefined,
  serviceGoal: string | undefined,
  serviceType: string | undefined,
): {
  [id in BeneficiaryTypes]: {
    [id in IndicatorSectionKey]: string | undefined;
  };
} => ({
  human: {
    capacityDevelopment: undefined,
    reach: `Define how an individual ${intermediaries ||
      beneficiaryTerm} must engage with ${programName} to be considered successfully reached.`,
    learn: `Briefly define what successful ${intermediaries ||
      beneficiaryTerm} learn from this program.`,
    act: 'Briefly define these successful actions or behaviors.',
    catalyzed: undefined,
    succeed: ' Briefly define the success threshold for this outcome.',
  },
  animal: {
    capacityDevelopment: undefined,
    reach: `Define how an animal must engage with ${programName} to be considered successfully reached.`,
    learn: undefined,
    act: undefined,
    catalyzed: undefined,
    succeed: undefined,
  },
  nonHuman: {
    capacityDevelopment: undefined,
    reach: `Define what ${indicator.name.toLowerCase()} means to ${programName}`,
    learn: undefined,
    act: undefined,
    catalyzed: undefined,
    succeed: undefined,
  },
});

export const getExampleText = (
  indicator: IndicatorWithUpdate | Indicator,
  sectionKey: IndicatorSectionKey,
  beneficiaryTerm: string | undefined,
): string | JSX.Element | undefined => {
  if (!indicator.isHuman) return undefined;
  const type = getIndicatorType(indicator);
  const text = exampleText[type][sectionKey];

  if (text.length > 0) {
    return (
      <TrueImpactText variant="body">
        {text.map((t, i) => (
          <li key={`${sectionKey}-${i}`}>{t}</li>
        ))}
      </TrueImpactText>
    );
  }

  switch (sectionKey) {
    case 'capacityDevelopment':
      return (
        <TrueImpactText variant="body">
          <li>
            Number of digital (e.g. information management systems, data
            repositories) or physical (e.g. building enhancements)
            infrastructures developed or enhanced to improve or expand program
            implementation.
          </li>
        </TrueImpactText>
      );
    case 'reach':
      return (
        <TrueImpactText variant="body">
          <li>
            {beneficiaryTerm} participate in at least 80% of the training
            sessions.
          </li>
          <li>
            {beneficiaryTerm} receive food assistance at least once a month for
            three consecutive months.{' '}
          </li>
          <li>{beneficiaryTerm} attend at least one counseling session</li>
        </TrueImpactText>
      );
    case 'learn':
      return (
        <TrueImpactText variant="body">
          <li>
            {beneficiaryTerm} improve their literacy skills by at least one
            level based on standardized assessments.
          </li>
          <li>
            {beneficiaryTerm} successfully develop a business plan by the end of
            the training.
          </li>
        </TrueImpactText>
      );
    case 'act':
      return (
        <TrueImpactText variant="body">
          <li>
            {beneficiaryTerm} apply to at least three job openings per week.
          </li>
          <li>{beneficiaryTerm} create and stick to a monthly budget.</li>
          <li>
            {beneficiaryTerm} regularly practice skills learned in our training
            sessions.
          </li>
        </TrueImpactText>
      );
    case 'succeed':
      return (
        <TrueImpactText variant="body">
          <li>
            {beneficiaryTerm} secure full-time, upwardly mobile employment at a
            living wage (per the MIT Living Wage Calculator).
          </li>
          <li>
            {beneficiaryTerm} observably improve their behavior, academic
            performance, and social skills.
          </li>
          <li>
            {beneficiaryTerm} achieving and maintaining a healthy weight, blood
            pressure, or blood sugar levels.
          </li>
        </TrueImpactText>
      );
    default:
      return undefined;
  }
};
